/**
 * Module dependencies.
 */

import { Box, color, media, units } from '@untile/react-components';
import { Button, Input, Password, RouterLink } from 'src/components/core';
import { GetServerSideProps, NextPage } from 'next/types';
import { authRoutes, myProductsRoutes } from 'src/core/routes';
import { createToken } from 'src/api/auth/create-token';
import { getNetworkErrorTranslatedKey } from 'src/core/utils/errors';
import { serverAuth } from 'src/core/utils/session';
import { theme } from 'styled-tools';
import { useAuth } from 'src/context/auth/context';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useRouter } from 'next/router';
import { useSnackbar } from 'src/context/snackbar/context';
import BaseAuthPage from 'src/components/pages/auth';
import Trans from 'next-translate/Trans';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import styled from 'styled-components';
import url from 'url';
import useTranslate from 'src/hooks/use-translate';

/**
 * `FormData` type.
 */

type FormData = {
  email: string;
  password: string;
};

/**
 * `Form` styled component.
 */

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

/**
 * `StyledInput` styled component.
 */

const StyledInput = styled(Input)`
  margin-bottom: ${units(3.5)};
`;

/**
 * `StyledPassword` styled component.
 */

const StyledPassword = styled(Password)`
  margin-bottom: ${units(4.5)};
`;

/**
 * `StyledRouterLink` styled component.
 */

const StyledRouterLink = styled(RouterLink)`
  color: ${color('black')};
  margin-bottom: ${units(2)};
  text-decoration: underline;
  transition: opacity ${theme('animations.defaultTransition')};

  &:focus,
  &:hover {
    opacity: 0.8;
  }

  &:first-of-type {
    margin-bottom: ${units(5)};

    ${media.min('md')`
      margin-bottom: ${units(2)};
    `}
  }
`;

/**
 * `SignIn` page.
 */

const SignIn: NextPage = () => {
  const router = useRouter();
  const { onUpdateToken } = useAuth();
  const { showMessage } = useSnackbar();
  const { translate } = useTranslate();
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset
  } = useForm({
    mode: 'onBlur'
  });

  const { isLoading: isSubmitting, mutateAsync } = useMutation(createToken);

  const handleSignIn = useCallback(
    (formData: FormData) => {
      return mutateAsync(formData, {
        onError: ({ status }) => {
          const errorCode = status === 401 ? 'unauthorized' : 'default';
          const translatedKey = getNetworkErrorTranslatedKey(
            'signIn',
            errorCode
          );

          showMessage(translate(`errors:${translatedKey}`), {
            appearance: 'error'
          });
        },
        onSuccess: ({ token }) => {
          onUpdateToken(token);
          reset();
          showMessage(translate('sign-in:successMessage'), {
            appearance: 'success'
          });

          const redirectUrl = get(router.query, 'redirectTo') as string;

          if (!isEmpty(redirectUrl)) {
            const urlTo = url.format({
              pathname: redirectUrl,
              query: {
                ...omit(router.query, ['redirectTo']),
                token
              }
            });

            window.location.href = urlTo;

            return;
          }

          router.push(myProductsRoutes.home);
        }
      });
    },
    [mutateAsync, onUpdateToken, reset, router, showMessage, translate]
  );

  return (
    <BaseAuthPage
      description={translate('sign-in:description')}
      title={translate('sign-in:title')}
    >
      <Box marginBottom={units(5)}>
        <Form onSubmit={handleSubmit(handleSignIn)}>
          <StyledInput
            disabled={isSubmitting}
            error={errors?.email}
            isRequired
            label={translate('forms:labels.email')}
            name={'email'}
            placeholder={translate('forms:placeholders.email')}
            {...register('email', {
              required: {
                message: translate('forms:rules.required'),
                value: true
              }
            })}
          />

          <StyledPassword
            disabled={isSubmitting}
            error={errors?.password}
            isRequired
            label={translate('forms:labels.password')}
            name={'password'}
            placeholder={translate('forms:placeholders.password')}
            {...register('password', {
              required: {
                message: translate('forms:rules.required'),
                value: true
              }
            })}
          />

          <StyledRouterLink href={authRoutes.password.recoverPassword}>
            {translate('sign-in:forgotPassword')}
          </StyledRouterLink>

          <Button
            isLoading={isSubmitting}
            type={'submit'}
          >
            {translate('common:actions.signIn')}
          </Button>
        </Form>
      </Box>

      <Trans
        components={[
          <StyledRouterLink
            href={authRoutes.signUp}
            key={'sign-up-link'}
          >
            {translate('common:actions.signUp')}
          </StyledRouterLink>
        ]}
        i18nKey={'sign-in:signUp'}
      />
    </BaseAuthPage>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps: GetServerSideProps = async ctx => {
  const { isAuthenticated } = await serverAuth(ctx);

  if (isAuthenticated) {
    return {
      redirect: {
        destination: myProductsRoutes.home,
        permanent: false
      }
    };
  }

  return {
    props: {}
  };
};

/**
 * Export `SignIn` page.
 */

export default SignIn;
